import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Layers,
  Plus,
  Key,
  Eye,
  Edit,
  Trash2,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  Zap,
  Copy,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { cn } from '../../utils/cn';
import alertService from '../../utils/alert';
import DashboardModuleShell from '../../components/DashboardModuleShell';
import FeatureLockedPage from '../../components/FeatureLockedPage';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import {
  fetchFlows,
  createFlow,
  publishFlow,
  deprecateFlow,
  deleteFlow,
  sendFlow,
  generateKeyPair,
  getPublicKey,
  uploadPublicKey,
  saveFlowsToken,
  syncToMeta,
} from '../../redux/whatsappFlowsSlice';

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────

const FLOW_CATEGORIES = [
  'SIGN_UP',
  'SIGN_IN',
  'APPOINTMENT_BOOKING',
  'LEAD_GENERATION',
  'CONTACT_US',
  'CUSTOMER_SUPPORT',
  'SURVEY',
  'OTHER',
];

const STATUS_TABS = ['All', 'draft', 'published', 'deprecated'];

// ─────────────────────────────────────────────────────────────
// Status helpers
// ─────────────────────────────────────────────────────────────

const getStatusStyle = (status) => {
  switch (status?.toLowerCase()) {
    case 'published':
      return 'bg-[#E1F2E8] text-[#04844B]';
    case 'draft':
      return 'bg-[#F4F6F9] text-[#706E6B]';
    case 'deprecated':
      return 'bg-[#FEE8E8] text-[#EA001E]';
    case 'throttled':
      return 'bg-[#FEF3E1] text-[#B95011]';
    case 'blocked':
      return 'bg-[#FEE8E8] text-[#EA001E]';
    default:
      return 'bg-[#F4F6F9] text-[#706E6B]';
  }
};

const getStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'published':
      return <CheckCircle size={10} />;
    case 'draft':
      return <Clock size={10} />;
    case 'deprecated':
    case 'blocked':
      return <XCircle size={10} />;
    case 'throttled':
      return <AlertTriangle size={10} />;
    default:
      return <Clock size={10} />;
  }
};

// ─────────────────────────────────────────────────────────────
// Create Flow Modal
// ─────────────────────────────────────────────────────────────

const defaultCreateForm = {
  name: '',
  categories: [],
  isDynamic: false,
  endpointUri: '',
  triggerType: 'none',
  keywords: '',
};

function CreateFlowModal({ onClose, onSubmit, loading }) {
  const [form, setForm] = useState(defaultCreateForm);

  const toggleCategory = (cat) => {
    setForm((prev) => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter((c) => c !== cat)
        : [...prev.categories, cat],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (form.categories.length === 0) {
      alertService.error('Select Category', 'Please select at least one category.');
      return;
    }
    const payload = {
      name: form.name.trim(),
      categories: form.categories,
      isDynamic: form.isDynamic,
      endpointUri: form.isDynamic ? form.endpointUri.trim() : undefined,
      trigger: {
        type: form.triggerType,
        keywords: form.triggerType === 'keyword'
          ? form.keywords.split(',').map((k) => k.trim()).filter(Boolean)
          : [],
      }
    };
    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/40 sm:p-4">
      <div className="bg-white w-full max-w-2xl rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] flex flex-col overflow-y-auto max-h-[100dvh]">
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 flex items-center justify-between border-b border-[#E5E5E5] shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#0176D3] text-white flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.16)] shadow-[#0176D3]/30">
              <Layers size={20} />
            </div>
            <div>
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">Create New Flow</h3>
              <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-widest mt-0.5">WhatsApp Flow Designer</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center hover:bg-white rounded transition-all border border-transparent hover:border-[#D8DDE6] text-[#706E6B]"
          >
            <Plus className="w-5 h-5 rotate-45" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden flex-1">
          <div className="flex-1 p-8 sm:p-10 overflow-y-auto space-y-8">
          {/* Name */}
          <div className="space-y-3">
            <label className="text-[11px] font-bold text-[#706E6B] flex items-center gap-2">
              <Layers size={10} className="text-[#0176D3]" /> Flow Name
            </label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Customer Onboarding"
              className="w-full px-6 py-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg text-[14px] font-bold outline-none focus:bg-white focus:border-[#0176D3] focus:ring-4 focus:ring-[#0176D3]/5 transition-all"
            />
          </div>

          {/* Categories */}
          <div className="space-y-3">
            <label className="text-[11px] font-bold text-[#706E6B] block">
              Categories
            </label>
            <div className="flex flex-wrap gap-2">
              {FLOW_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => toggleCategory(cat)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest border transition-all',
                    form.categories.includes(cat)
                      ? 'bg-[#0176D3] border-[#0176D3] text-white shadow-[0_2px_8px_rgba(0,0,0,0.16)] shadow-[#0176D3]/20'
                      : 'bg-white border-[#D8DDE6] text-[#706E6B] hover:border-[#0176D3] hover:text-[#0176D3]'
                  )}
                >
                  {cat.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Type: Static vs Dynamic */}
          <div className="space-y-3">
            <label className="text-[11px] font-bold text-[#706E6B] block">Flow Type</label>
            <div className="flex gap-4">
              {[
                { id: false, label: 'Static (Navigate)', desc: 'Screen-to-screen navigation, no server needed' },
                { id: true, label: 'Dynamic (Data Exchange)', desc: 'Real-time server endpoint required' },
              ].map((type) => (
                <button
                  key={String(type.id)}
                  type="button"
                  onClick={() => setForm({ ...form, isDynamic: type.id })}
                  className={cn(
                    'flex-1 p-5 rounded-lg border-2 text-left transition-all',
                    form.isDynamic === type.id
                      ? 'bg-[#0176D3]/5 border-[#0176D3] text-[#0176D3]'
                      : 'bg-white border-[#EEF1F6] text-[#706E6B] hover:border-[#D8DDE6]'
                  )}
                >
                  <p className="text-[12px] font-bold uppercase tracking-widest">{type.label}</p>
                  <p className="text-[10px] font-medium mt-1 leading-relaxed">{type.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Endpoint URI (dynamic only) */}
          {form.isDynamic && (
            <div className="space-y-3">
              <label className="text-[11px] font-bold text-[#706E6B] block">Endpoint URI</label>
              <input
                type="url"
                required
                value={form.endpointUri}
                onChange={(e) => setForm({ ...form, endpointUri: e.target.value })}
                placeholder="https://crm.waapleads.com/api/whatsapp-flows/endpoint?phoneNumberId=YOUR_PHONE_ID"
                className="w-full px-6 py-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg text-[13px] font-medium outline-none focus:bg-white focus:border-[#0176D3] transition-all"
              />
            </div>
          )}

          {/* Trigger Type */}
          <div className="space-y-3">
            <label className="text-[11px] font-bold text-[#706E6B] block">Trigger Type</label>
            <div className="flex gap-3">
              {['none', 'always', 'keyword'].map((tt) => (
                <button
                  key={tt}
                  type="button"
                  onClick={() => setForm({ ...form, triggerType: tt })}
                  className={cn(
                    'flex-1 py-3 rounded-lg border-2 text-[11px] font-bold uppercase tracking-widest transition-all',
                    form.triggerType === tt
                      ? 'bg-[#0176D3]/5 border-[#0176D3] text-[#0176D3]'
                      : 'bg-white border-[#EEF1F6] text-[#706E6B] hover:border-[#D8DDE6]'
                  )}
                >
                  {tt}
                </button>
              ))}
            </div>
          </div>

          {/* Keywords */}
          {form.triggerType === 'keyword' && (
            <div className="space-y-3">
              <label className="text-[11px] font-bold text-[#706E6B] block">
                Keywords <span className="normal-case font-medium">(comma-separated)</span>
              </label>
              <input
                type="text"
                value={form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                placeholder="sign up, register, join"
                className="w-full px-6 py-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg text-[14px] font-medium outline-none focus:bg-white focus:border-[#0176D3] transition-all"
              />
            </div>
          )}

          {/* Footer */}
          </div>
          <div className="p-6 sm:px-10 sm:py-6 border-t border-[#EEF1F6] bg-white shrink-0 flex justify-end gap-4">
            <Button
              variant="outline"
              type="button"
              onClick={onClose}
              className="h-14 px-8 border-none hover:bg-[#FDE7E9] hover:text-[#BA0517]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              className="h-14 px-12 slds-button-brand shadow-[0_2px_8px_rgba(0,0,0,0.16)] shadow-[#0176D3]/20"
            >
              Create Flow
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Send Flow Modal
// ─────────────────────────────────────────────────────────────

const defaultSendForm = {
  to: '',
  bodyText: '',
  headerText: '',
  ctaButtonText: 'Open Form',
  mode: 'published',
  metaFlowId: '',
};

function SendFlowModal({ flow, onClose, onSubmit, loading }) {
  const [form, setForm] = useState({ ...defaultSendForm, metaFlowId: flow.metaFlowId || '' });
  const hasMetaId = !!(form.metaFlowId || flow.metaFlowId);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.to.trim() || !form.bodyText.trim()) return;
    if (!hasMetaId) return;
    onSubmit({
      flowId: flow._id,
      to: form.to.trim(),
      bodyText: form.bodyText.trim(),
      headerText: form.headerText.trim() || undefined,
      ctaButtonText: form.ctaButtonText.trim() || 'Open Form',
      mode: form.mode,
      metaFlowId: (form.metaFlowId || flow.metaFlowId || '').trim(),
    });
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/40 sm:p-4">
      <div className="bg-white w-full max-w-xl max-h-[100dvh] rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] flex flex-col overflow-y-auto">
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 flex items-center justify-between border-b border-[#E5E5E5] shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#0176D3] text-white flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.16)] shadow-[#0176D3]/30">
              <Send size={18} />
            </div>
            <div>
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">Send Flow</h3>
              <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-widest mt-0.5 truncate max-w-[200px]">{flow.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center hover:bg-white rounded transition-all border border-transparent hover:border-[#D8DDE6] text-[#706E6B]"
          >
            <Plus className="w-5 h-5 rotate-45" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden flex-1">
          <div className="p-8 sm:p-10 space-y-6 overflow-y-auto flex-1">
          {/* Meta Flow ID — required, shown as warning if missing */}
          {!flow.metaFlowId && (
            <div className="space-y-2">
              <div className="p-3 bg-[#FEF3E1] border border-[#F2C47B] rounded-lg flex items-start gap-2">
                <AlertTriangle size={14} className="text-[#B95011] mt-0.5 shrink-0" />
                <p className="text-[11px] text-[#B95011] font-medium leading-relaxed">
                  This flow hasn't been synced to Meta yet. Enter your Meta Flow ID below (from Meta's Flow Manager), or sync the flow first.
                </p>
              </div>
              <label className="text-[11px] font-bold text-[#706E6B] block">Meta Flow ID <span className="text-[#BA0517]">*</span></label>
              <input
                type="text"
                required
                value={form.metaFlowId}
                onChange={(e) => setForm({ ...form, metaFlowId: e.target.value })}
                placeholder="e.g. 1234567890123456"
                className="w-full px-6 py-4 bg-[#F4F6F9] border border-[#F2C47B] rounded-lg text-[14px] font-mono outline-none focus:bg-white focus:border-[#0176D3] transition-all"
              />
            </div>
          )}

          {/* Recipient */}
          <div className="space-y-2">
            <label className="text-[11px] font-bold text-[#706E6B] block">
              Recipient Phone <span className="normal-case font-medium text-[#706E6B]">(with country code, e.g. +91...)</span>
            </label>
            <input
              type="text"
              required
              value={form.to}
              onChange={(e) => setForm({ ...form, to: e.target.value })}
              placeholder="+919876543210"
              className="w-full px-6 py-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg text-[14px] font-bold outline-none focus:bg-white focus:border-[#0176D3] focus:ring-4 focus:ring-[#0176D3]/5 transition-all"
            />
          </div>

          {/* Header Text */}
          <div className="space-y-2">
            <label className="text-[11px] font-bold text-[#706E6B] block">Header Text <span className="normal-case font-medium">(optional)</span></label>
            <input
              type="text"
              value={form.headerText}
              onChange={(e) => setForm({ ...form, headerText: e.target.value })}
              placeholder="e.g. Complete your profile"
              className="w-full px-6 py-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg text-[14px] font-medium outline-none focus:bg-white focus:border-[#0176D3] transition-all"
            />
          </div>

          {/* Body Text */}
          <div className="space-y-2">
            <label className="text-[11px] font-bold text-[#706E6B] block">Body Text <span className="text-[#BA0517]">*</span></label>
            <textarea
              rows={3}
              required
              value={form.bodyText}
              onChange={(e) => setForm({ ...form, bodyText: e.target.value })}
              placeholder="Message shown below the flow button…"
              className="w-full px-6 py-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg text-[14px] font-medium outline-none focus:bg-white focus:border-[#0176D3] transition-all resize-none"
            />
          </div>

          {/* CTA Button Text */}
          <div className="space-y-2">
            <label className="text-[11px] font-bold text-[#706E6B] block">CTA Button Text</label>
            <input
              type="text"
              value={form.ctaButtonText}
              onChange={(e) => setForm({ ...form, ctaButtonText: e.target.value })}
              placeholder="Open Form"
              className="w-full px-6 py-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg text-[14px] font-bold outline-none focus:bg-white focus:border-[#0176D3] transition-all"
            />
          </div>

          {/* Mode */}
          <div className="space-y-2">
            <label className="text-[11px] font-bold text-[#706E6B] block">Send Mode</label>
            <div className="flex gap-3">
              {['published', 'draft'].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setForm({ ...form, mode: m })}
                  className={cn(
                    'flex-1 py-3 rounded-lg border-2 text-[11px] font-bold uppercase tracking-widest transition-all',
                    form.mode === m
                      ? 'bg-[#0176D3]/5 border-[#0176D3] text-[#0176D3]'
                      : 'bg-white border-[#EEF1F6] text-[#706E6B] hover:border-[#D8DDE6]'
                  )}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          </div>
          <div className="p-6 sm:px-10 sm:py-6 border-t border-[#EEF1F6] bg-white shrink-0 flex justify-end gap-4">
            <Button variant="outline" type="button" onClick={onClose} className="h-12 px-8 border-none hover:bg-[#FDE7E9] hover:text-[#BA0517]">Cancel</Button>
            <Button type="submit" loading={loading} className="h-12 px-10 slds-button-brand shadow-[0_2px_8px_rgba(0,0,0,0.16)] shadow-[#0176D3]/20">
              Send Flow
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Manage Keys Modal
// ─────────────────────────────────────────────────────────────

function ManageKeysModal({ publicKey, onClose, onGenerate, onUpload, onSaveToken, loading }) {
  const keyText = publicKey?.publicKey || publicKey || '';
  const [flowsToken, setFlowsToken] = React.useState('');
  const [savingToken, setSavingToken] = React.useState(false);

  const handleCopy = () => {
    if (!keyText) return;
    navigator.clipboard.writeText(keyText).then(() => {
      alertService.toast('success', 'Copied!');
    });
  };

  const handleSaveToken = async () => {
    if (!flowsToken.trim()) return;
    setSavingToken(true);
    try {
      await onSaveToken(flowsToken.trim());
      setFlowsToken('');
      alertService.success('Token Saved', 'Flows API token saved. You can now upload your public key to Meta.');
    } catch (err) {
      alertService.error('Save Failed', err || 'Could not save token.');
    } finally {
      setSavingToken(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/40 sm:p-4">
      <div className="bg-white w-full max-w-xl max-h-[100dvh] rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] flex flex-col overflow-y-auto">
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 flex items-center justify-between border-b border-[#E5E5E5] shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#0176D3] text-white flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.16)] shadow-[#0176D3]/30">
              <Key size={20} />
            </div>
            <div>
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">Manage Encryption Keys</h3>
              <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-widest mt-0.5">WhatsApp Flows Key Pair</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center hover:bg-white rounded transition-all border border-transparent hover:border-[#D8DDE6] text-[#706E6B]"
          >
            <Plus className="w-5 h-5 rotate-45" />
          </button>
        </div>

        <div className="flex flex-col overflow-hidden flex-1">
          <div className="p-8 sm:p-10 space-y-6 overflow-y-auto flex-1">
          {/* Public Key Display */}
          {keyText ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-bold text-[#706E6B]">Current Public Key</label>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#F4F6F9] border border-[#D8DDE6] text-[10px] font-bold text-[#706E6B] hover:border-[#0176D3] hover:text-[#0176D3] transition-all  whitespace-nowrap"
                >
                  <Copy size={11} /> Copy
                </button>
              </div>
              <textarea
                readOnly
                rows={6}
                value={keyText}
                className="w-full px-5 py-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[11px] font-mono text-[#080707] outline-none resize-none leading-relaxed"
              />
            </div>
          ) : (
            <div className="p-6 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg text-center">
              <Key size={32} className="text-[#706E6B] mx-auto mb-3" />
              <p className="text-[13px] font-bold text-[#080707] mb-1">No Key Pair Found</p>
              <p className="text-[12px] text-[#706E6B] font-medium">Generate a key pair to enable encrypted WhatsApp Flows.</p>
            </div>
          )}

          {/* Permission requirement warning */}
          <div className="p-4 bg-[#FEF3E1] border border-[#F2C47B] rounded-lg flex items-start gap-3">
            <AlertTriangle size={15} className="text-[#B95011] mt-0.5 shrink-0" />
            <div className="text-[11px] text-[#B95011] leading-relaxed space-y-1">
              <p className="font-bold">Requires a System User access token</p>
              <p>Your current token may only have <code className="bg-[#FDEECB] px-1 rounded">whatsapp_business_messaging</code> permission. The Flows API additionally requires <code className="bg-[#FDEECB] px-1 rounded">whatsapp_business_management</code>.</p>
              <p>Go to <strong>Meta Business Manager → System Users → Generate Token</strong> and include both permissions.</p>
            </div>
          </div>

          {/* Flows API Token input */}
          <div className="space-y-2">
            <label className="text-[11px] font-bold text-[#706E6B] flex items-center gap-2">
              <Key size={11} /> Flows API Token (System User Token)
            </label>
            <p className="text-[11px] text-[#706E6B]">
              Your existing messaging token is used for sending messages. Paste a <strong>System User token</strong> with <code className="bg-[#F4F6F9] px-1 rounded">whatsapp_business_management</code> permission here — used only for Flows API calls.
            </p>
            <div className="flex gap-2">
              <input
                type="password"
                value={flowsToken}
                onChange={e => setFlowsToken(e.target.value)}
                placeholder="EAAxxxxx... (System User token)"
                className="flex-1 px-4 py-2.5 border border-[#D8DDE6] rounded-lg text-[12px] font-mono outline-none focus:border-[#0176D3] bg-[#F4F6F9]"
              />
              <button
                onClick={handleSaveToken}
                disabled={!flowsToken.trim() || savingToken}
                className="px-4 py-2.5 bg-[#0176D3] text-white text-[11px] font-bold rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#0165b8] transition-colors whitespace-nowrap"
              >
                {savingToken ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          {/* Info banner */}
          <div className="p-4 bg-[#EBF5FE] border border-[#B3D4F5] rounded-lg flex items-start gap-3">
            <AlertTriangle size={16} className="text-[#0176D3] mt-0.5 shrink-0" />
            <p className="text-[12px] font-medium text-[#0176D3] leading-relaxed">
              <strong>Two-step setup:</strong> First generate a key pair, then upload the public key to Meta so they can encrypt flow payloads sent to your endpoint.
            </p>
          </div>

          {/* Step indicators */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-5 h-5 rounded-full bg-[#0176D3] text-white text-[10px] font-bold flex items-center justify-center">1</span>
                <span className="text-[11px] font-bold text-[#080707] uppercase tracking-widest">Generate</span>
              </div>
              <p className="text-[11px] text-[#706E6B] leading-relaxed">Create a 2048-bit RSA key pair. Private key stored securely on your server.</p>
            </div>
            <div className={`p-4 border rounded-lg ${keyText ? 'bg-[#F4F6F9] border-[#D8DDE6]' : 'bg-[#F9F9F9] border-dashed border-[#D8DDE6] opacity-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center ${keyText ? 'bg-[#04844B]' : 'bg-[#706E6B]'}`}>2</span>
                <span className="text-[11px] font-bold text-[#080707] uppercase tracking-widest">Upload to Meta</span>
              </div>
              <p className="text-[11px] text-[#706E6B] leading-relaxed">Register the public key with Meta via Graph API so flows are encrypted end-to-end.</p>
            </div>
          </div>

          </div>
          <div className="p-6 sm:px-10 sm:py-6 border-t border-[#EEF1F6] bg-white shrink-0 flex justify-end gap-3">
            <Button variant="outline" type="button" onClick={onClose} className="h-11 px-6 border-none hover:bg-[#FDE7E9] hover:text-[#BA0517]">Close</Button>
            <Button
              type="button"
              onClick={onGenerate}
              loading={loading}
              icon={Key}
              className="h-11 px-6 bg-white border border-[#0176D3] text-[#0176D3] hover:bg-[#EBF5FE] shadow-none"
            >
              Generate Key Pair
            </Button>
            <Button
              type="button"
              onClick={onUpload}
              loading={loading}
              disabled={!keyText}
              icon={CheckCircle}
              className="h-11 px-6 slds-button-brand shadow-[0_2px_8px_rgba(0,0,0,0.16)] shadow-[#0176D3]/20 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Upload to Meta
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Flow Card
// ─────────────────────────────────────────────────────────────

function FlowCard({ flow, onEdit, onSend, onPublish, onDeprecate, onDelete, onPreview, onSync, actionLoading }) {
  const status = flow.status?.toLowerCase() || 'draft';
  const keywords = Array.isArray(flow.keywords) ? flow.keywords : [];

  return (
    <Card className="p-0 overflow-hidden bg-white border-[#D8DDE6] shadow-sm hover:shadow-[0_2px_8px_rgba(0,0,0,0.16)] hover:border-[#0176D3] transition-all group flex flex-col h-full">
      <div className="p-6 flex-1 flex flex-col">
        {/* Top row */}
        <div className="flex justify-between items-start gap-3 mb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <Layers size={11} className="text-[#0176D3]" />
              <span className="text-[9px] font-bold text-[#706E6B] uppercase tracking-widest">WhatsApp Flow</span>
            </div>
            <h3 className="text-[14px] font-bold text-[#080707] group-hover:text-[#0176D3] transition-colors line-clamp-1" title={flow.name}>
              {flow.name}
            </h3>
          </div>
          <div className={cn('flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest shrink-0', getStatusStyle(status))}>
            {getStatusIcon(status)}
            {status}
          </div>
        </div>

        {/* Categories */}
        {Array.isArray(flow.categories) && flow.categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {flow.categories.map((cat) => (
              <span key={cat} className="px-2 py-0.5 bg-[#0176D3]/5 border border-[#0176D3]/20 text-[#0176D3] text-[9px] font-bold uppercase tracking-tight rounded-full">
                {cat.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}

        {/* Dynamic badge */}
        {flow.isDynamic && (
          <div className="flex items-center gap-1.5 mb-4">
            <span className="flex items-center gap-1 px-2 py-0.5 bg-[#FEF3E1] border border-[#F2C47B] text-[#B95011] text-[9px] font-bold uppercase tracking-widest rounded-full">
              <Zap size={9} /> Dynamic Flow
            </span>
          </div>
        )}

        {/* Keywords */}
        {keywords.length > 0 && (
          <div className="mb-4">
            <p className="text-[9px] font-bold text-[#706E6B] uppercase tracking-widest mb-1.5">Triggers</p>
            <p className="text-[11px] font-medium text-[#080707] truncate">
              {keywords.join(', ')}
            </p>
          </div>
        )}

        {/* Meta sync status */}
        <div className="mb-3">
          {flow.metaFlowId ? (
            <div className="flex items-center gap-1.5 text-[10px] text-[#04844B] font-bold">
              <CheckCircle size={10} />
              <span className="truncate">Synced · {flow.metaFlowId}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-[10px] text-[#B95011] font-bold">
              <AlertTriangle size={10} />
              <span>Not synced to Meta</span>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="mt-auto pt-4 border-t border-[#EEF1F6] flex flex-wrap gap-2">
          <button
            onClick={() => onPreview(flow)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#F4F6F9] border border-[#D8DDE6] text-[#706E6B] hover:border-[#0176D3] hover:text-[#0176D3] transition-all whitespace-nowrap"
          >
            <Eye size={12} /> Preview
          </button>
          <button
            onClick={() => onEdit(flow)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#F4F6F9] border border-[#D8DDE6] text-[#706E6B] hover:border-[#0176D3] hover:text-[#0176D3] transition-all whitespace-nowrap"
          >
            <Edit size={12} /> Edit
          </button>
          {!flow.metaFlowId && (
            <button
              onClick={() => onSync(flow)}
              disabled={actionLoading}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#EBF5FE] border border-[#0176D3]/30 text-[#0176D3] hover:bg-[#0176D3] hover:text-white transition-all disabled:opacity-50 whitespace-nowrap"
            >
              <RefreshCw size={12} /> Sync to Meta
            </button>
          )}
          <button
            onClick={() => onSend(flow)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#F4F6F9] border border-[#D8DDE6] text-[#706E6B] hover:border-[#0176D3] hover:text-[#0176D3] transition-all whitespace-nowrap"
          >
            <Send size={12} /> Send
          </button>
          {status === 'draft' && (
            <button
              onClick={() => onPublish(flow)}
              disabled={actionLoading}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#E1F2E8] border border-[#04844B]/20 text-[#04844B] hover:bg-[#04844B] hover:text-white transition-all disabled:opacity-50 whitespace-nowrap"
            >
              <CheckCircle size={12} /> Publish
            </button>
          )}
          {status === 'published' && (
            <button
              onClick={() => onDeprecate(flow)}
              disabled={actionLoading}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#FEF3E1] border border-[#B95011]/20 text-[#B95011] hover:bg-[#B95011] hover:text-white transition-all disabled:opacity-50 whitespace-nowrap"
            >
              <XCircle size={12} /> Deprecate
            </button>
          )}
          {status === 'draft' && (
            <button
              onClick={() => onDelete(flow)}
              disabled={actionLoading}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#FEE8E8] border border-[#EA001E]/20 text-[#EA001E] hover:bg-[#EA001E] hover:text-white transition-all disabled:opacity-50 whitespace-nowrap"
            >
              <Trash2 size={12} /> Delete
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// Feature Gate — shown when plan doesn't include whatsapp_flows
// ─────────────────────────────────────────────────────────────

function WhatsAppFlowsFeatureGate({ plan }) {
  return (
    <FeatureLockedPage
      title="WhatsApp Flows"
      subtitle="Meta-native interactive forms & multi-screen experiences"
      icon={Layers}
      plan={plan}
      featureName="WhatsApp Flows"
      description="Upgrade to unlock interactive multi-screen experiences, lead capture forms, and appointment booking directly inside WhatsApp."
      highlights={[
        { icon: Layers, label: 'Multi-screen flows', desc: 'Build rich interactive UIs inside WhatsApp' },
        { icon: Zap, label: 'Dynamic data', desc: 'Real-time server-powered form logic' },
        { icon: Send, label: 'Send to customers', desc: 'Trigger flows from campaigns or automation' },
      ]}
    />
  );
}

// ─────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────

const WhatsAppFlowsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { hasAccess, plan, loading: featureLoading } = useFeatureAccess('whatsapp_flows');

  const flows = useSelector((state) => state.whatsappFlows?.flows ?? []);
  const loading = useSelector((state) => state.whatsappFlows?.loading ?? false);
  const publicKey = useSelector((state) => state.whatsappFlows?.publicKey ?? null);

  const [activeTab, setActiveTab] = useState('All');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeysModal, setShowKeysModal] = useState(false);
  const [sendModalFlow, setSendModalFlow] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // On mount
  useEffect(() => {
    dispatch(fetchFlows());
    dispatch(getPublicKey());
  }, [dispatch]);

  // Filtered flows
  const filteredFlows = activeTab === 'All'
    ? flows
    : flows.filter((f) => f.status?.toLowerCase() === activeTab.toLowerCase());

  // Stats
  const stats = [
    { label: 'Total Flows', value: flows.length, icon: Layers, color: 'text-[#0176D3]' },
    { label: 'Published', value: flows.filter((f) => f.status?.toLowerCase() === 'published').length, icon: CheckCircle, color: 'text-[#04844B]' },
    { label: 'Drafts', value: flows.filter((f) => f.status?.toLowerCase() === 'draft').length, icon: Clock, color: 'text-[#B95011]' },
    { label: 'Dynamic', value: flows.filter((f) => f.isDynamic).length, icon: Zap, color: 'text-[#0176D3]' },
  ];

  // Handlers
  const handleCreateFlow = useCallback(async (payload) => {
    setActionLoading(true);
    try {
      await dispatch(createFlow(payload)).unwrap();
      setShowCreateModal(false);
      alertService.success('Flow Created', 'Your WhatsApp Flow has been created successfully.');
    } catch (err) {
      alertService.error('Creation Failed', err || 'Could not create the flow.');
    } finally {
      setActionLoading(false);
    }
  }, [dispatch]);

  const handlePublish = useCallback(async (flow) => {
    const confirmed = await alertService.confirm(
      'Publish Flow?',
      `"${flow.name}" will be published and visible to users.`,
      'question',
      'Yes, Publish'
    );
    if (!confirmed) return;
    setActionLoading(true);
    try {
      await dispatch(publishFlow(flow._id)).unwrap();
      alertService.success('Published!', 'Flow is now live.');
    } catch (err) {
      alertService.error('Publish Failed', err || 'Could not publish the flow.');
    } finally {
      setActionLoading(false);
    }
  }, [dispatch]);

  const handleDeprecate = useCallback(async (flow) => {
    const confirmed = await alertService.confirm(
      'Deprecate Flow?',
      `"${flow.name}" will be deprecated and no longer accessible to users.`,
      'warning',
      'Yes, Deprecate'
    );
    if (!confirmed) return;
    setActionLoading(true);
    try {
      await dispatch(deprecateFlow(flow._id)).unwrap();
      alertService.success('Deprecated', 'Flow has been deprecated.');
    } catch (err) {
      alertService.error('Failed', err || 'Could not deprecate the flow.');
    } finally {
      setActionLoading(false);
    }
  }, [dispatch]);

  const handleDelete = useCallback(async (flow) => {
    const confirmed = await alertService.confirm(
      'Delete Flow?',
      `"${flow.name}" will be permanently deleted. This cannot be undone.`,
      'warning',
      'Yes, Delete'
    );
    if (!confirmed) return;
    setActionLoading(true);
    try {
      await dispatch(deleteFlow(flow._id)).unwrap();
      alertService.success('Deleted', 'Flow has been removed.');
    } catch (err) {
      alertService.error('Delete Failed', err || 'Could not delete the flow.');
    } finally {
      setActionLoading(false);
    }
  }, [dispatch]);

  const handleSendFlow = useCallback(async (payload) => {
    setActionLoading(true);
    try {
      await dispatch(sendFlow(payload)).unwrap();
      setSendModalFlow(null);
      alertService.success('Flow Sent!', 'The flow message has been dispatched successfully.');
    } catch (err) {
      alertService.error('Send Failed', err || 'Could not send the flow.');
    } finally {
      setActionLoading(false);
    }
  }, [dispatch]);

  const handleGenerateKeyPair = useCallback(async () => {
    const confirmed = await alertService.confirm(
      'Generate New Key Pair?',
      'This will replace your existing key pair. You will need to upload the new public key to Meta Business Manager.',
      'warning',
      'Generate'
    );
    if (!confirmed) return;
    setActionLoading(true);
    try {
      await dispatch(generateKeyPair()).unwrap();
      alertService.success('Key Pair Generated', 'Upload the public key to Meta Business Manager.');
    } catch (err) {
      alertService.error('Generation Failed', err || 'Could not generate key pair.');
    } finally {
      setActionLoading(false);
    }
  }, [dispatch]);

  const handleSync = useCallback(async (flow) => {
    setActionLoading(true);
    try {
      await dispatch(syncToMeta(flow._id)).unwrap();
      alertService.success('Synced!', `Flow "${flow.name}" is now registered with Meta and ready to send.`);
    } catch (err) {
      alertService.error('Sync Failed', err || 'Could not sync flow to Meta.');
    } finally {
      setActionLoading(false);
    }
  }, [dispatch]);

  const handleSaveFlowsToken = useCallback(async (token) => {
    await dispatch(saveFlowsToken(token)).unwrap();
  }, [dispatch]);

  const handleUploadPublicKey = useCallback(async () => {
    setActionLoading(true);
    try {
      await dispatch(uploadPublicKey()).unwrap();
      alertService.success('Public Key Registered!', 'Your public key has been uploaded to Meta. WhatsApp Flows encryption is now active.');
    } catch (err) {
      alertService.error('Upload Failed', err || 'Could not upload public key to Meta.');
    } finally {
      setActionLoading(false);
    }
  }, [dispatch]);

  const handlePreview = useCallback((flow) => {
    alertService.success('Flow Preview', `Use Meta's Flow Preview Tool or the WhatsApp Business App to preview "${flow.name}".`);
  }, []);

  const handleEdit = useCallback((flow) => {
    navigate(`/dashboard/whatsapp-flows/builder/${flow._id}`);
  }, [navigate]);

  // Feature loading state
  if (featureLoading) {
    return (
      <DashboardModuleShell
        title="WhatsApp Flows"
        subtitle="Meta-native interactive forms & multi-screen experiences"
        icon={Layers}
      >
        <div className="flex flex-col items-center justify-center py-32 bg-white rounded-lg border border-[#D8DDE6] shadow-sm">
          <RefreshCw className="w-10 h-10 animate-spin text-[#0176D3] mb-5" />
          <p className="text-[13px] font-bold text-[#706E6B] uppercase tracking-widest">Checking plan access…</p>
        </div>
      </DashboardModuleShell>
    );
  }

  // Feature not in plan — show upgrade gate
  if (!hasAccess) {
    return <WhatsAppFlowsFeatureGate plan={plan} />;
  }

  return (
    <DashboardModuleShell
      title="WhatsApp Flows"
      subtitle="Meta-native interactive forms & multi-screen experiences"
      icon={Layers}
      stats={stats}
      actions={
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            icon={Key}
            onClick={() => setShowKeysModal(true)}
          >
            Manage Keys
          </Button>
          <Button
            onClick={() => setShowCreateModal(true)}
            icon={Plus}
            className="slds-button-brand"
          >
            New Flow
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Status Filter Tabs */}
        <div className="flex items-center gap-3 pb-2 overflow-x-auto no-scrollbar">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                'px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap border',
                activeTab === tab
                  ? 'bg-[#0176D3] border-[#0176D3] text-white shadow-[0_2px_8px_rgba(0,0,0,0.16)] shadow-[#0176D3]/20'
                  : 'bg-white border-[#D8DDE6] text-[#706E6B] hover:border-[#0176D3] hover:text-[#0176D3]'
              )}
            >
              {tab}
              {tab !== 'All' && (
                <span className="ml-1.5 opacity-70">
                  ({flows.filter((f) => f.status?.toLowerCase() === tab.toLowerCase()).length})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Flows Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 bg-white rounded-lg border border-[#D8DDE6] shadow-sm">
            <RefreshCw className="w-12 h-12 animate-spin text-[#0176D3] mb-6" />
            <p className="text-[13px] font-bold text-[#706E6B] uppercase tracking-widest">Loading WhatsApp Flows…</p>
          </div>
        ) : filteredFlows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 bg-white rounded-lg border border-[#D8DDE6] shadow-sm">
            <div className="w-20 h-20 rounded-lg bg-[#F4F6F9] border border-[#D8DDE6] flex items-center justify-center mb-6">
              <Layers className="w-10 h-10 text-[#706E6B]" />
            </div>
            <h3 className="text-lg font-bold text-[#080707] mb-2 tracking-tight">
              {activeTab === 'All' ? 'No Flows Found' : `No ${activeTab} flows`}
            </h3>
            <p className="text-[13px] text-[#706E6B] mb-8 max-w-sm text-center font-medium leading-relaxed">
              {activeTab === 'All'
                ? 'Create your first WhatsApp Flow to start building interactive multi-screen experiences for your customers.'
                : `You have no ${activeTab} flows at the moment.`}
            </p>
            {activeTab === 'All' && (
              <Button onClick={() => setShowCreateModal(true)} icon={Plus} variant="outline" className="px-8">
                Create your first WhatsApp Flow
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredFlows.map((flow) => (
              <FlowCard
                key={flow._id}
                flow={flow}
                onEdit={handleEdit}
                onSend={setSendModalFlow}
                onPublish={handlePublish}
                onDeprecate={handleDeprecate}
                onDelete={handleDelete}
                onPreview={handlePreview}
                onSync={handleSync}
                actionLoading={actionLoading}
              />
            ))}
          </div>
        )}

        {/* Info Banner */}
        <div className="p-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg flex items-start gap-4">
          <Layers className="text-[#0176D3] mt-0.5 shrink-0" size={20} />
          <div>
            <h4 className="text-[13px] font-bold text-[#080707]">WhatsApp Flows Compliance</h4>
            <p className="text-[12px] font-medium text-[#706E6B] leading-relaxed mb-0">
              All flows must comply with Meta's Business Policy. Dynamic flows require a secure HTTPS endpoint and an uploaded public encryption key. Published flows cannot be edited — deprecate and create a new version instead.
            </p>
          </div>
        </div>
      </div>

      {/* Create Flow Modal */}
      {showCreateModal && (
        <CreateFlowModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateFlow}
          loading={actionLoading}
        />
      )}

      {/* Send Flow Modal */}
      {sendModalFlow && (
        <SendFlowModal
          flow={sendModalFlow}
          onClose={() => setSendModalFlow(null)}
          onSubmit={handleSendFlow}
          loading={actionLoading}
        />
      )}

      {/* Manage Keys Modal */}
      {showKeysModal && (
        <ManageKeysModal
          publicKey={publicKey}
          onClose={() => setShowKeysModal(false)}
          onGenerate={handleGenerateKeyPair}
          onUpload={handleUploadPublicKey}
          onSaveToken={handleSaveFlowsToken}
          loading={actionLoading}
        />
      )}
    </DashboardModuleShell>
  );
};

export default WhatsAppFlowsPage;
